import { supabase } from '@/lib/supabase';

export type CachedSerpAnalysis = {
  id: string;
  projectId: string;
  keywordId: string;
  keyword: string;
  status: string;
  serpResults: any;
  summary: any;
  errorMessage: string | null;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
};

export type CachedContentOptimization = {
  id: string;
  projectId: string;
  keywordId: string;
  pageUrl: string;
  overallScore: number | null;
  scores: any;
  recommendations: any;
  status: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
};

export type CachedInternalLinkSuggestions = {
  id: string;
  projectId: string;
  keywordId: string;
  targetPage: string;
  suggestions: any;
  status: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
};

function mapSerpRow(row: any): CachedSerpAnalysis {
  return {
    id: row.id,
    projectId: row.project_id,
    keywordId: row.keyword_id,
    keyword: row.keyword,
    status: row.status,
    serpResults: row.serp_results,
    summary: row.summary,
    errorMessage: row.error_message,
    createdBy: row.created_by,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
  };
}

function mapOptimizationRow(row: any): CachedContentOptimization {
  return {
    id: row.id,
    projectId: row.project_id,
    keywordId: row.keyword_id,
    pageUrl: row.page_url,
    overallScore: row.overall_score,
    scores: row.scores,
    recommendations: row.recommendations,
    status: row.status,
    createdBy: row.created_by,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
  };
}

function mapLinkRow(row: any): CachedInternalLinkSuggestions {
  return {
    id: row.id,
    projectId: row.project_id,
    keywordId: row.keyword_id,
    targetPage: row.target_page,
    suggestions: row.suggestions,
    status: row.status,
    createdBy: row.created_by,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
  };
}

const seoAiCacheService = {
  async getSerpAnalysis(keywordId: string): Promise<CachedSerpAnalysis | null> {
    const { data, error } = await supabase
      .from('seo_serp_analyses')
      .select('*')
      .eq('keyword_id', keywordId)
      .eq('status', 'complete')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    return mapSerpRow(data);
  },

  async saveSerpAnalysis(params: {
    projectId: string;
    keywordId: string;
    keyword: string;
    serpResults: any;
    summary: any;
    status: string;
    errorMessage?: string;
  }): Promise<CachedSerpAnalysis> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('seo_serp_analyses')
      .insert({
        project_id: params.projectId,
        keyword_id: params.keywordId,
        keyword: params.keyword,
        serp_results: params.serpResults,
        summary: params.summary,
        status: params.status,
        error_message: params.errorMessage ?? null,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return mapSerpRow(data);
  },

  async getContentOptimization(keywordId: string): Promise<CachedContentOptimization | null> {
    const { data, error } = await supabase
      .from('seo_content_optimizations')
      .select('*')
      .eq('keyword_id', keywordId)
      .eq('status', 'complete')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    return mapOptimizationRow(data);
  },

  async saveContentOptimization(params: {
    projectId: string;
    keywordId: string;
    pageUrl: string;
    overallScore: number | null;
    scores: any;
    recommendations: any;
    status: string;
  }): Promise<CachedContentOptimization> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('seo_content_optimizations')
      .insert({
        project_id: params.projectId,
        keyword_id: params.keywordId,
        page_url: params.pageUrl,
        overall_score: params.overallScore,
        scores: params.scores,
        recommendations: params.recommendations,
        status: params.status,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return mapOptimizationRow(data);
  },

  async getInternalLinkSuggestions(keywordId: string): Promise<CachedInternalLinkSuggestions | null> {
    const { data, error } = await supabase
      .from('seo_internal_link_suggestions')
      .select('*')
      .eq('keyword_id', keywordId)
      .eq('status', 'complete')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    return mapLinkRow(data);
  },

  async saveInternalLinkSuggestions(params: {
    projectId: string;
    keywordId: string;
    targetPage: string;
    suggestions: any;
    status: string;
  }): Promise<CachedInternalLinkSuggestions> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('seo_internal_link_suggestions')
      .insert({
        project_id: params.projectId,
        keyword_id: params.keywordId,
        target_page: params.targetPage,
        suggestions: params.suggestions,
        status: params.status,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return mapLinkRow(data);
  },
};

export default seoAiCacheService;
